import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

export function useRevealAccounts({ enabled, endpoint }) {
    const [revealedAccounts, setRevealedAccounts] = useState({});
    const [loadingIds, setLoadingIds] = useState(new Set());
    const timersRef = useRef(new Map());

    useEffect(() => {
        return () => {
            timersRef.current.forEach((timer) => clearTimeout(timer));
            timersRef.current.clear();
        };
    }, []);

    const isRevealLoading = useCallback((id) => loadingIds.has(id), [loadingIds]);
    const getRevealedAccount = useCallback((id) => revealedAccounts[id], [revealedAccounts]);

    const revealAccount = useCallback(async (id) => {
        if (!enabled || !id) return;

        const reason = window.prompt('원문 계좌 조회 사유를 입력해주세요. (3자 이상)');
        if (reason === null) return;
        if (reason.trim().length < 3) {
            alert('조회 사유는 3자 이상 입력해주세요.');
            return;
        }

        setLoadingIds(prev => new Set(prev).add(id));
        try {
            const response = await axios.post(
                process.env.REACT_APP_API + endpoint,
                { ids: [id], reason: reason.trim() },
                { withCredentials: true }
            );

            const tmp = response?.data;
            if (tmp?.code === 1000) {
                const account = tmp?.data?.accounts?.[0];
                if (!account) {
                    alert('조회된 원문 계좌 정보가 없습니다.');
                    return;
                }

                setRevealedAccounts(prev => ({
                    ...prev,
                    [account.id]: account,
                }));

                const expiresIn = Number(tmp?.data?.expires_in || 60);
                const prevTimer = timersRef.current.get(account.id);
                if (prevTimer) clearTimeout(prevTimer);
                const timer = setTimeout(() => {
                    setRevealedAccounts(prev => {
                        const next = { ...prev };
                        delete next[account.id];
                        return next;
                    });
                    timersRef.current.delete(account.id);
                }, expiresIn * 1000);
                timersRef.current.set(account.id, timer);
            } else {
                alert(tmp?.msg || '원문 계좌 조회에 실패했습니다.');
            }
        } catch (error) {
            console.error('원문 계좌 조회 오류:', error);
            alert('원문 계좌 조회 중 오류가 발생했습니다.');
        } finally {
            setLoadingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    }, [enabled, endpoint]);

    return {
        getRevealedAccount,
        isRevealLoading,
        revealAccount,
    };
}

export function renderRevealAccountCell({ enabled, id, value, revealed, loading, onReveal }) {
    if (!enabled) return value || '';

    return (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", width: "100%" }}>
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                {value || ''}
            </span>
            {revealed ? (
                <span style={{ color: "#059669", fontSize: "11px", fontWeight: "600", whiteSpace: "nowrap" }}>
                    원문
                </span>
            ) : (
                <button
                    type="button"
                    onClick={() => onReveal(id)}
                    disabled={loading}
                    style={{
                        padding: "2px 6px",
                        fontSize: "11px",
                        borderRadius: "4px",
                        border: "1px solid #64748b",
                        backgroundColor: loading ? "#e5e7eb" : "#f8fafc",
                        color: loading ? "#94a3b8" : "#334155",
                        cursor: loading ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                    }}
                >
                    {loading ? '조회중' : '원문'}
                </button>
            )}
        </div>
    );
}

export function useRevealSplitAccounts({ enabled, endpoint }) {
    const [revealedSplitAccounts, setRevealedSplitAccounts] = useState({});
    const [loadingIds, setLoadingIds] = useState(new Set());
    const timersRef = useRef(new Map());

    useEffect(() => {
        return () => {
            timersRef.current.forEach((timer) => clearTimeout(timer));
            timersRef.current.clear();
        };
    }, []);

    const isSplitRevealLoading = useCallback((id) => loadingIds.has(id), [loadingIds]);
    const getRevealedSplitAccount = useCallback((id) => revealedSplitAccounts[id], [revealedSplitAccounts]);

    const revealSplitAccount = useCallback(async (id) => {
        if (!enabled || !id) return;

        const reason = window.prompt('원문 분할 계좌 조회 사유를 입력해주세요. (3자 이상)');
        if (reason === null) return;
        if (reason.trim().length < 3) {
            alert('조회 사유는 3자 이상 입력해주세요.');
            return;
        }

        setLoadingIds(prev => new Set(prev).add(id));
        try {
            const response = await axios.post(
                process.env.REACT_APP_API + endpoint,
                { ids: [id], reason: reason.trim() },
                { withCredentials: true }
            );

            const tmp = response?.data;
            if (tmp?.code === 1000) {
                const splitAccount = tmp?.data?.splitAccounts?.[0];
                if (!splitAccount) {
                    alert('조회된 원문 분할 계좌 정보가 없습니다.');
                    return;
                }

                setRevealedSplitAccounts(prev => ({
                    ...prev,
                    [splitAccount.log_id]: splitAccount,
                }));

                const expiresIn = Number(tmp?.data?.expires_in || 60);
                const prevTimer = timersRef.current.get(splitAccount.log_id);
                if (prevTimer) clearTimeout(prevTimer);
                const timer = setTimeout(() => {
                    setRevealedSplitAccounts(prev => {
                        const next = { ...prev };
                        delete next[splitAccount.log_id];
                        return next;
                    });
                    timersRef.current.delete(splitAccount.log_id);
                }, expiresIn * 1000);
                timersRef.current.set(splitAccount.log_id, timer);
            } else {
                alert(tmp?.msg || '원문 분할 계좌 조회에 실패했습니다.');
            }
        } catch (error) {
            console.error('원문 분할 계좌 조회 오류:', error);
            alert('원문 분할 계좌 조회 중 오류가 발생했습니다.');
        } finally {
            setLoadingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    }, [enabled, endpoint]);

    return {
        getRevealedSplitAccount,
        isSplitRevealLoading,
        revealSplitAccount,
    };
}
